import type { ChatProvider, FastRegisterType } from '@/domain/account';
import type { DepositBonusSettings } from '@/domain/bonuses';
import type { PlayerReward } from '@/domain/rewards';
import { apiUrl } from './config';
import { ApiError } from './errors';
import { apiTransport } from './transport';

/**
 * Where the legacy client kept the player's token. The unprefixed key is a
 * deliberate continuity choice so an existing session survives the migration —
 * not an oversight. Same-origin XSS (and any other app on this origin) can read
 * `localStorage.login`. Moving to an httpOnly cookie is a Phase C decision.
 */
const USER_TOKEN_KEY = 'login';
const USER_SETTINGS_KEY = 'user';

/** Shape of the OAuth response from the users host. */
interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  /** RFC 1123 timestamp, e.g. `Tue, 18 Aug 2026 13:00:00 GMT`. */
  '.expires'?: string;
  /** JSON-encoded brand configuration, present on the client-credentials grant. */
  accountSettings?: string;
  /** JSON-encoded player profile, present on the password grant. */
  userSettings?: string;
}

/** The brand configuration the whole app is scoped to. */
export interface Account {
  Guid: string;
  Name?: string;
  /** Brand display name used for the document `<title>` (legacy `Account.Title`). */
  Title?: string;
  ShowLogo?: boolean;
  ShowFooter?: boolean;
  AllowUserRegistration?: boolean;
  /** When true, registration requires SMS verification before bonuses / full login. */
  VerifySMS?: boolean;
  /** When true, client enforces complex password rules on register. */
  ComplexPassword?: boolean;
  /** Account countries for register (id/text/currencyId/style ISO2). */
  Countries?: AccountCountry[] | null;
  /** Account currencies for register dropdown lookup. */
  Currencies?: AccountCurrency[] | null;
  AllowDeposits?: boolean;
  AllowWithdrawals?: boolean;
  AllowSelfExclusion?: boolean;
  AllowDepositSoftLimits?: boolean;
  AddressInRegistration?: boolean;
  /** When true, registration requires a citizen / passport ID. */
  CitizenIdInRegister?: boolean;
  /** When true, use the slim fast-register form instead of full Register v2. */
  FastRegister?: boolean;
  /** Which identifier(s) fast register asks for (`FastRegisterType`). */
  FastRegisterType?: FastRegisterType;
  AccountDomainId?: string | number;
  DomainImages?: {
    Logo?: string | null;
    Favicon?: string | null;
    [key: string]: unknown;
  } | null;
  FeedType?: number;
  UnderMaintenance?: boolean;
  RequireLoginScreen?: boolean;
  /** When true, the brand exposes live chat support. */
  Chat?: boolean;
  /** Vendor credential / license / embed key (legacy `Account.ChatKey`). */
  ChatKey?: string | null;
  /** Which live-chat vendor to load (`ChatProvider` enum). */
  ChatProvider?: ChatProvider;
  Languages?: AccountLanguage[];
  Language?: AccountLanguageInfo;
  /** Prematch top-event IDs used by homepage Top Events (`EventIds` filter). */
  TopEvents?: number[] | null;
  TopEventsLive?: number[] | null;
  WidgetEvents?: number[] | null;
  HomePrematchEvents?: number[] | null;
  HomeLiveEvents?: number[] | null;
  WidgetBannerEvents?: number[] | null;
  /** LMT provider: 0=None, 1=Betradar, 2=TheSports */
  LiveMatchTrackingType?: number;
  /** Betradar client ID, or TheSports profile key(s). */
  LiveMatchTrackingKey?: string;
  /** Master switch for live stream embeds. */
  LiveStream?: boolean;
  /** Provider base URL for live stream (e.g. TheSports host). */
  LiveStreamUrl?: string | null;
  /** Token passed to the stream provider URL. */
  LiveStreamKey?: string | null;
  /** When true, only one outcome per event is allowed on the ticket. */
  DisableMultiComboTicket?: boolean;
  BetSettings?: BetSettings;
  /** Brand modules (e.g. `{ Name: 'betbuilder' }`) — legacy `EnabledModules` / `Modules`. */
  EnabledModules?: AccountModule[] | null;
  Modules?: AccountModule[] | null;
  /** When true, casino lobby/list is browsable without a player session. */
  ShowGamesWithoutLogin?: boolean;
  /** Currency used for anonymous casino catalogue filters. */
  DefaultCurrencyId?: number | null;
  /** Default country for anonymous CMS / popup filters (legacy `Account.CountryId`). */
  CountryId?: number | string | null;
  /** Provider launch metadata (SmartSoft / Altira LaunchUrl, ApiId, …). */
  AccountProvidersInfo?: AccountProviderInfo[] | null;
  [key: string]: unknown;
}

/** Row from account `AccountProvidersInfo` used to compose provider launch URLs. */
export interface AccountProviderInfo {
  Name?: string | null;
  LaunchUrl?: string | null;
  ApiId?: string | null;
  [key: string]: unknown;
}

/** Account / user feature module flag from auth settings. */
export interface AccountModule {
  Name?: string | null;
  [key: string]: unknown;
}

/** Country row from account bootstrap (`DropDownCountry`). */
export interface AccountCountry {
  id: string;
  text: string;
  /** Currency id for this country (legacy `currencyId`). */
  currencyId: string;
  /** ISO2 country code for phone input (legacy `style` = `Country.Short`). */
  style: string;
}

/** Currency row from account bootstrap (`CurrencyItem`). */
export interface AccountCurrency {
  id: string;
  text: string;
  sign?: string;
  value?: number;
}

/** Countries for register UI — drops the synthetic "Default" entry. */
export function accountCountries(source: Account | null | undefined): AccountCountry[] {
  if (!Array.isArray(source?.Countries)) return [];
  return source.Countries.filter(
    (country) =>
      country &&
      typeof country.id === 'string' &&
      country.id.length > 0 &&
      country.text !== 'Default',
  );
}

/** Currency row for a country id, or null when missing. */
export function currencyForCountry(
  source: Account | null | undefined,
  countryId: string | number | null | undefined,
): AccountCurrency | null {
  if (countryId == null || countryId === '') return null;
  const countries = accountCountries(source);
  const country = countries.find((item) => String(item.id) === String(countryId));
  if (!country?.currencyId) return null;
  const currencies = Array.isArray(source?.Currencies) ? source.Currencies : [];
  return currencies.find((item) => String(item.id) === String(country.currencyId)) ?? null;
}

/** Stake / odds / system limits from account or user settings. */
export interface BetSettings {
  MinimumSystem?: number;
  MaximumSystem?: number;
  MaximumCombi?: number;
  MaximumCombiLive?: number;
  MinimumBet?: number;
  MinimumBetLive?: number;
  MaximumBet?: number;
  MaximumBetLive?: number;
  MaximumOdds?: number;
  MaximumOddsLive?: number;
  MaximumWin?: number;
  MaximumWinLive?: number;
  AllowCashout?: boolean;
  AllowCashoutBonus?: boolean;
  AllowAutoCashout?: boolean;
  AllowPartialCashout?: boolean;
  Options?: string;
  LiveTicketDelay?: number;
  PrematchTicketDelay?: number;
  [key: string]: unknown;
}

/** Event IDs for homepage Top Events — reads `Account.TopEvents`. */
export function accountTopEventIds(source: Account | null | undefined): number[] {
  if (!Array.isArray(source?.TopEvents) || source.TopEvents.length === 0) return [];
  return source.TopEvents.filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
}

export interface AccountLanguageInfo {
  Short: string;
  Name?: string;
}

export interface AccountLanguage extends AccountLanguageInfo {
  Default?: boolean;
  Position?: number;
}

/** Player wallet balances from LoginResponse.Credits. */
export interface CreditsData {
  WithdrawableCredits?: number;
  RestrictedCredits?: number;
  BonusCredits?: number;
  CasinoBonusCredits?: number;
  ExternalSportsbookBonusCredits?: number;
}

/**
 * Player profile from the password-grant `userSettings` payload
 * (UsersAPI `LoginResponse`).
 */
export interface UserSettings {
  Success?: boolean;
  Id?: string;
  Name?: string;
  Username?: string;
  Guid?: string;
  SessionGuid?: string;
  Currency?: string;
  CurrencyCode?: string;
  CurrCode?: string;
  CurrencyId?: number | null;
  CountryId?: number | null;
  CountryShort?: string;
  Language?: string;
  Telephone?: string;
  Email?: string;
  Membership?: number;
  SmsVerification?: boolean;
  Credits?: CreditsData;
  NumberOfDecimals?: number;
  BetSettings?: BetSettings;
  /** UsersAPI `CashoutBetSettings` from login — offer calc + allow flags. */
  CashoutSettings?: CashoutSettings;
  /** UsersAPI `CreditBetSettings` — KreditBet eligibility + optional terms URL. */
  CreditBetSettings?: CreditBetSettings;
  /** Active FreeBet / BackupBet rewards from login (`PlayerRewards`). */
  Rewards?: PlayerReward[] | null;
  /** Active sportsbook deposit/wager bonus settings (socket `DepositBonusSettings`). */
  SportsbookBonus?: DepositBonusSettings | null;
  /** Active casino deposit/wager bonus settings (socket `DepositBonusSettings`). */
  CasinoBonus?: DepositBonusSettings | null;
  /** Player-level modules when account EnabledModules is empty (legacy fallback). */
  Modules?: AccountModule[] | null;
  /** Wallet id passed to WalletAPI game session endpoints. */
  WalletId?: string | null;
  /** Count of favourited casino games (drives Favorites category visibility). */
  FavoriteGamesCount?: number | null;
  /** Hall / admin id some providers require on session create. */
  HallId?: string | null;
  /** Shop / site id — legacy session `Account: 'SId-' + SId`. */
  SId?: string | number | null;
  /** Manager id — legacy matgaming `ManagerId`. */
  MId?: string | number | null;
  [key: string]: unknown;
}

/** UsersAPI `CashoutBetSettings` (login `CashoutSettings`). */
export interface CashoutSettings {
  AllowCashout?: boolean;
  AllowCashoutBonus?: boolean;
  AutoCashout?: boolean;
  PartialCashout?: boolean;
  CashoutMaximumOdds?: number;
  /** 0 Fair, 1 Managed — matches UsersAPI `CashoutModes`. */
  CashoutMode?: number;
  CashoutSpeedLevel?: number;
  CashoutProfitPercent?: number;
}

/** UsersAPI `CreditBetSettings` from login (`TicketCreditBetSettings`). */
export interface CreditBetSettings {
  AllowCreditBet?: boolean;
  /** Optional CMS / terms page URL for KreditBet. */
  PageUrl?: string | null;
}

function normalizeLanguageCode(code: string): string {
  return code.trim().toLowerCase();
}

function compareAccountLanguages(a: AccountLanguage, b: AccountLanguage): number {
  const posA = typeof a.Position === 'number' ? a.Position : Number.POSITIVE_INFINITY;
  const posB = typeof b.Position === 'number' ? b.Position : Number.POSITIVE_INFINITY;
  if (posA !== posB) return posA - posB;

  return (a.Name ?? a.Short).localeCompare(b.Name ?? b.Short);
}

/**
 * Returns account languages sorted by `Position` then display name, deduped by
 * the normalized short code.
 */
export function getAccountLanguages(source: Account | null | undefined): AccountLanguage[] {
  if (!source?.Languages?.length) return [];

  const dedupedByCode = new Map<string, AccountLanguage>();
  for (const language of source.Languages) {
    if (!language || typeof language.Short !== 'string') continue;
    const short = language.Short.trim();
    if (!short) continue;

    const normalizedCode = normalizeLanguageCode(short);
    dedupedByCode.set(normalizedCode, {
      ...language,
      Short: short,
      Name: language.Name?.trim() || short,
    });
  }

  return [...dedupedByCode.values()].sort(compareAccountLanguages);
}

/**
 * Resolves account default language in priority order:
 * 1) first `Languages[].Default === true`
 * 2) `Language.Short` compatibility field
 * 3) fallback `'en'`
 */
export function getDefaultAccountLanguage(source: Account | null | undefined): string {
  const languages = getAccountLanguages(source);
  const explicitDefault = languages.find((language) => language.Default === true);
  if (explicitDefault) return explicitDefault.Short;

  const compatibilityLanguage = source?.Language?.Short;
  if (typeof compatibilityLanguage === 'string' && compatibilityLanguage.trim()) {
    return compatibilityLanguage.trim();
  }

  return 'en';
}

interface AppToken {
  accessToken: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

let appToken: AppToken | null = null;
let account: Account | null = null;
/** Shared so concurrent callers wait on one token request rather than racing. */
let bootstrapPromise: Promise<Account> | null = null;
/** Shared so concurrent callers wait on one app-token request rather than racing. */
let tokenPromise: Promise<TokenResponse> | null = null;

const unauthorizedHandlers = new Set<() => void>();
const authListeners = new Set<() => void>();
let authVersion = 0;

function notifyAuthListeners(): void {
  authVersion += 1;
  for (const listener of authListeners) listener();
}

/** Subscribe to player session changes (login / logout / unauthorized). */
export function subscribeAuth(listener: () => void): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

/** Snapshot for `useSyncExternalStore` — bumps whenever the session changes. */
export function getAuthVersion(): number {
  return authVersion;
}

/**
 * Registers a callback for when the server rejects our credentials. The API layer
 * stays free of routing concerns; the app decides where to send the player.
 */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

type SessionStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function readStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(token: string, user: UserSettings, persist: boolean): void {
  const primary: SessionStore = persist ? localStorage : sessionStorage;
  const secondary: SessionStore = persist ? sessionStorage : localStorage;

  try {
    secondary.removeItem(USER_TOKEN_KEY);
    secondary.removeItem(USER_SETTINGS_KEY);
  } catch {
    // Secondary store may be unavailable.
  }

  try {
    primary.setItem(USER_TOKEN_KEY, token);
    primary.setItem(USER_SETTINGS_KEY, JSON.stringify(user));
  } catch {
    // The session still works for this page load without persistence.
  }
}

/** True when the player token lives in `localStorage` (keep-me-logged-in). */
function isSessionPersisted(): boolean {
  try {
    if (localStorage.getItem(USER_TOKEN_KEY) !== null) return true;
    if (sessionStorage.getItem(USER_TOKEN_KEY) !== null) return false;
  } catch {
    // Fall through.
  }
  return true;
}

/**
 * Patches `Credits` on the stored player profile (player-socket credit pushes).
 * No-op when there is no active session.
 */
export function updateUserCredits(credits: CreditsData): void {
  const token = getUserToken();
  const user = getUserSettings();
  if (!token || !user) return;

  // Merge so a partial socket/API payload cannot wipe other credit buckets.
  writeSession(
    token,
    { ...user, Credits: { ...user.Credits, ...credits } },
    isSessionPersisted(),
  );
  notifyAuthListeners();
}

/**
 * Replaces `Rewards` on the stored player profile (login seed + Reward socket).
 * No-op when there is no active session.
 */
export function updateUserRewards(rewards: PlayerReward[]): void {
  const token = getUserToken();
  const user = getUserSettings();
  if (!token || !user) return;

  writeSession(token, { ...user, Rewards: rewards }, isSessionPersisted());
  notifyAuthListeners();
}

/**
 * Patches SportsbookBonus / CasinoBonus from socket or lost-bonus claim.
 * No-op when there is no active session.
 */
export function updateUserDepositBonusSettings(patch: {
  SportsbookBonus?: DepositBonusSettings | null;
  CasinoBonus?: DepositBonusSettings | null;
}): void {
  const token = getUserToken();
  const user = getUserSettings();
  if (!token || !user) return;

  const next: UserSettings = { ...user };
  if ('SportsbookBonus' in patch) next.SportsbookBonus = patch.SportsbookBonus ?? null;
  if ('CasinoBonus' in patch) next.CasinoBonus = patch.CasinoBonus ?? null;
  writeSession(token, next, isSessionPersisted());
  notifyAuthListeners();
}

function clearStoredSession(): void {
  for (const store of [localStorage, sessionStorage] as SessionStore[]) {
    try {
      store.removeItem(USER_TOKEN_KEY);
      store.removeItem(USER_SETTINGS_KEY);
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }
}

export function getUserToken(): string | null {
  return readStorageItem(USER_TOKEN_KEY);
}

export function getUserSettings(): UserSettings | null {
  const raw = readStorageItem(USER_SETTINGS_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as UserSettings;
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return getUserToken() !== null;
}

/** Drops the player session but keeps the app-level token, which is brand-wide. */
export function clearSession(): void {
  clearStoredSession();
  notifyAuthListeners();
}

export function handleUnauthorized(): void {
  clearStoredSession();
  notifyAuthListeners();
  for (const handler of unauthorizedHandlers) handler();
}

/** Drops a stale app-level token so the next caller re-requests client_credentials. */
export function invalidateAppToken(): void {
  appToken = null;
  tokenPromise = null;
}

/**
 * Posts to the OAuth endpoint. It always takes form encoding and never carries a
 * bearer token, so it bypasses the regular client.
 */
async function requestToken(body: Record<string, string>): Promise<TokenResponse> {
  const url = apiUrl('users', 'token');
  let response: { status: number; statusText: string; data: unknown };

  try {
    response = await apiTransport.request({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      data: new URLSearchParams(body).toString(),
      validateStatus: () => true,
    });
  } catch (cause) {
    throw new ApiError({ status: 0, statusText: 'Network error', url, body: String(cause) });
  }

  const payload = response.data;

  if (response.status < 200 || response.status >= 300) {
    throw new ApiError({
      status: response.status,
      statusText: response.statusText,
      url,
      body: payload,
    });
  }

  return payload as TokenResponse;
}

function storeAppToken(response: TokenResponse): AppToken {
  const expiresAt = response['.expires']
    ? Date.parse(response['.expires'])
    : Date.now() + (response.expires_in ?? 0) * 1000;

  appToken = {
    accessToken: response.access_token,
    // Refresh a minute early so a request never travels with a just-expired token.
    expiresAt: (Number.isNaN(expiresAt) ? Date.now() + 3_600_000 : expiresAt) - 60_000,
  };
  return appToken;
}

/**
 * Client-credentials grant, single-flight. Token expiry is independent of the
 * cached account, so renewal does not go through `bootstrapAccount`.
 */
function requestAppToken(): Promise<TokenResponse> {
  if (!tokenPromise) {
    const pending = requestToken({ grant_type: 'client_credentials' })
      .then((response) => {
        storeAppToken(response);
        return response;
      })
      .finally(() => {
        if (tokenPromise === pending) tokenPromise = null;
      });
    tokenPromise = pending;
  }
  return tokenPromise;
}

/**
 * Exchanges the brand's client credentials for an app-level token and the account
 * configuration. Everything else depends on `Account.Guid`, so this runs first.
 */
export function bootstrapAccount(): Promise<Account> {
  if (account) return Promise.resolve(account);

  bootstrapPromise ??= (async () => {
    try {
      const response = await requestAppToken();

      if (!response.accountSettings) {
        throw new Error('Token response did not include accountSettings');
      }

      const parsed = JSON.parse(response.accountSettings) as Account;
      if (!parsed.Guid) throw new Error('Account configuration is missing Guid');

      account = parsed;
      return parsed;
    } catch (error) {
      // Let the next caller try again rather than caching the failure.
      bootstrapPromise = null;
      throw error;
    }
  })();

  return bootstrapPromise;
}

export function getAccount(): Account | null {
  return account;
}

export async function getAccountId(): Promise<string> {
  return (account ?? (await bootstrapAccount())).Guid;
}

/**
 * The token used for `Authorization`. A signed-in player's token takes precedence;
 * otherwise the app-level token is used and renewed when it ages out.
 */
export async function getAuthToken(): Promise<string> {
  const userToken = getUserToken();
  if (userToken) return userToken;

  if (!appToken || appToken.expiresAt <= Date.now()) {
    await requestAppToken();
  }

  if (!appToken) throw new Error('Unable to obtain an application token');
  return appToken.accessToken;
}

export interface LoginResult {
  token: string;
  user: UserSettings;
  /** True when SMS verification is required before the session is usable. */
  requiresSmsVerification?: boolean;
}

export interface LoginOptions {
  /**
   * When true (default), persist to `localStorage`. When false, use
   * `sessionStorage` so the session ends with the browser tab.
   */
  persist?: boolean;
}

/** Password grant, matching the legacy login form. */
export async function login(
  username: string,
  password: string,
  options: LoginOptions = {},
): Promise<LoginResult> {
  const persist = options.persist !== false;
  const accountId = await getAccountId();

  const response = await requestToken({
    grant_type: 'password',
    account: accountId,
    Username: username,
    Password: password,
  });

  const token = response.access_token;
  if (typeof token !== 'string' || token.length === 0) {
    throw new ApiError({
      status: 401,
      statusText: 'Unauthorized',
      url: apiUrl('users', 'token'),
      body: response,
    });
  }

  let user: UserSettings;
  try {
    user = response.userSettings ? (JSON.parse(response.userSettings) as UserSettings) : {};
  } catch {
    throw new ApiError({
      status: 401,
      statusText: 'Unauthorized',
      url: apiUrl('users', 'token'),
      body: response.userSettings,
    });
  }

  if (user.Success === false) {
    throw new ApiError({
      status: 401,
      statusText: 'Unauthorized',
      url: apiUrl('users', 'token'),
      body: user,
    });
  }

  // Legacy: SmsVerification means the player must verify before the token is kept.
  if (user.SmsVerification === true) {
    return { token, user, requiresSmsVerification: true };
  }

  writeSession(token, user, persist);
  notifyAuthListeners();

  return { token, user };
}

/** Test seam: resets the module's cached account and tokens. */
export function resetAuthState(): void {
  appToken = null;
  account = null;
  bootstrapPromise = null;
  tokenPromise = null;
}
